import {
    observer
} from 'mobx-react-lite';
import type { ApplicationSdk } from '../../stores';
import { useEffect, useRef } from 'react';
import OrderManagerStore from '../../stores/manager/OrderManagerStore';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import OrderDialogStore from '../../stores/manager/dialog/OrderDialogStore';
import OrderEditDialogStore from '../../stores/manager/dialog/OrderEditDialogStore';
import { formatDate } from '../../helper';
import { Toolbar } from 'primereact/toolbar';
import SearchField from '../../components/SearchField';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import { Chip } from 'primereact/chip';
import { statusOptions } from '../../constant';

const NewOrder = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    return <>
        <Button
            label="Neu"
            icon="pi pi-plus"
            aria-label="Filter"
            onClick={() => sdk.openDialog(OrderEditDialogStore, {})}
            style={{ marginRight: '0.5rem' }}
        />
    </>
});

const RefreshOrders = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    return <>
        <Button
            icon="pi pi-sync"
            aria-label="Aktualisieren"
            onClick={() => sdk.currentStore.refresh()}
            style={{ marginRight: '0.5rem' }}
            disabled={!!sdk.currentStore?.refreshTimer}
        />
    </>
});

const FilterChips = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const selectedManager = sdk.currentStore?.filterManager || null;
    const selectedCleaner = sdk.currentStore?.filterCleaner || null;
    const selectedStatus = sdk.currentStore?.filterStatus || null;
    const selectedSearch = sdk.currentStore?.searchValue || '';

    return (
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {selectedManager && (
                <Chip
                    label={`Objektleiter: ${selectedManager.name}`}
                    removable
                    onRemove={() => sdk.currentStore.applyFilterManager(null)}
                />
            )}
            {selectedCleaner && (
                <Chip
                    label={`Reinigungskraft: ${selectedCleaner.name}`}
                    removable
                    onRemove={() => sdk.currentStore.applyFilterCleaner(null)}
                />
            )}
            {selectedStatus && (
                <Chip
                    label={`Status: ${statusOptions.find((status: any) => status.value === selectedStatus)?.label}`}
                    removable
                    onRemove={() => sdk.currentStore.applyFilterStatus(null)}
                />
            )}
            {selectedSearch && (
                <Chip
                    label={`Suche: ${selectedSearch}`}
                    removable
                    onRemove={() => sdk.currentStore.clearSearch()}
                />
            )}
        </div>
    );
});

const FilterPopover = observer(({ sdk }: { sdk: ApplicationSdk }) => {

    // const [selectedManager, setSelectedManager] = useState(null);

    const loadManagers = async () => {
        if (sdk.currentStore) {
            sdk.currentStore.fetchAvailableManagers();
        }
    };

    const onManagerChange = (e: any) => {
        sdk.currentStore.applyFilterManager(e.value);
    };

    // const loadCleaner = async () => {
    //     if (sdk.currentStore) {
    //         sdk.currentStore.fetchAvailableCleaner();
    //     }
    // };

    // const onCleanerChange = (e: any) => {
    //     sdk.currentStore.applyFilterCleaner(e.value);
    // };

    // const loadObjects = async () => {
    //     if (sdk.currentStore) {
    //         sdk.currentStore.fetchAvailableObjects();
    //     }
    // };

    // const onObjectChange = (e: any) => {
    //     sdk.currentStore.applyFilterObject(e.value);
    // };

    const onStatusChange = (e: any) => {
        sdk.currentStore.applyFilterStatus(e.value);
    };

    return (
        <div>
            <h4 style={{ marginBottom: '1rem' }}>Status</h4>
            <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center' }}>
                <Dropdown
                    value={sdk.currentStore?.filterStatus || null}
                    options={statusOptions}
                    onChange={onStatusChange}
                    placeholder="Status auswählen"
                    optionLabel="label"
                    loading={false}
                    style={{ width: '100%' }}
                />
            </div>
            <h4 style={{ marginBottom: '1rem' }}>Objektleiter auswählen</h4>
            <Dropdown
                value={sdk.currentStore?.filterManager || null}
                options={sdk.currentStore?.available_managers || []}
                onChange={onManagerChange}
                onShow={loadManagers} // Lazy load managers when dropdown is focused
                placeholder="Objektleiter auswählen"
                optionLabel="name"
                loading={false}
                style={{ width: '100%' }}
            />
            {/* <h4 style={{ marginBottom: '1rem' }}>Reinigungskraft auswählen</h4>
            <Dropdown
                value={sdk.currentStore?.filterCleaner || null}
                options={sdk.currentStore?.available_cleaner || []}
                onChange={onCleanerChange}
                onShow={loadCleaner} // Lazy load managers when dropdown is focused
                placeholder="Reinigungskraft auswählen"
                optionLabel="name"
                loading={false}
                style={{ width: '100%' }}
            /> */}
            {/* <h4 style={{ marginBottom: '1rem' }}>Objekt auswählen</h4>
            <Dropdown
                value={sdk.currentStore?.filterObject || null}
                options={sdk.currentStore?.available_objects || []}
                onChange={onObjectChange}
                onShow={loadObjects} // Lazy load managers when dropdown is focused
                placeholder="Objekt auswählen"
                optionLabel="name"
                loading={false}
                style={{ width: '100%' }}
            /> */}

        </div>
    );
});

const SearchToolbar = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const ref: any = useRef(null);

    return <>
        <Toolbar start={() => <>
            <NewOrder sdk={sdk} />
            <RefreshOrders sdk={sdk} />
        </>}
            center={() => (
                <>
                    <Button
                        icon="pi pi-filter"
                        className="p-button-text"
                        label="Filter"
                        onClick={(e: any) => ref.current?.toggle(e)}
                        aria-label="Filter"
                    />
                    <OverlayPanel ref={ref} style={{ width: '300px' }}>
                        <FilterPopover sdk={sdk} />
                    </OverlayPanel>
                </>
            )}
            end={() => <SearchField store={sdk.currentStore} placeholder={'Bestellungen durchsuchen'} />} />
        <FilterChips sdk={sdk} />
    </>
});

const OrderStatus = ({ status }: any) => {
    let useStatusText: string = 'NEU';
    let useStatusSeverity: any = 'success';

    if (status === 'COMPLETED') {
        useStatusText = 'FERTIG';
        useStatusSeverity = 'info';
    } else if (status === 'ARCHIVED') {
        useStatusText = 'ARCHIVIERT';
        useStatusSeverity = 'warning';
    }
    return <Tag value={useStatusText} severity={useStatusSeverity}></Tag>;
}

const OrderOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(OrderManagerStore);

        // Funktion zum Aktualisieren der Daten
        const refreshOnTabActive = () => {
            if (document.visibilityState === 'visible') {
                sdk.currentStore.refresh(); // Aktualisiere die Daten
            }
        };

        // Event-Listener für Sichtbarkeitsänderungen hinzufügen
        document.addEventListener('visibilitychange', refreshOnTabActive);

        // Cleanup: Event-Listener entfernen
        return () => {
            document.removeEventListener('visibilitychange', refreshOnTabActive);
        };
    }, [sdk]);

    const orders = sdk.currentStore?.allOrders || null;

    const onPageChange = (event: any) => {
        sdk.currentStore.onPageChange(event.page + 1);
    };

    return <div className="card">
        <DataTable
            value={orders}
            emptyMessage={'Keine Bestellungen gefunden'}
            header={<SearchToolbar sdk={sdk} />}
            onSort={(event: any) => {
                sdk.currentStore.onSort(event.sortField);
                return event;
            }}
            sortOrder={sdk.currentStore?.sortOrder || 1}
            sortField={sdk.currentStore?.sortField || 'id'}
            rowHover={true}
            loading={!!sdk.currentStore?.orderListLoading}
        // editMode={'row'}#
        >
            {/* <Column header="Image" body={imageBodyTemplate}></Column> */}
            <Column field="status" header="Status" style={{ textAlign: 'center' }} body={(p: any) => OrderStatus(p)}></Column>
            <Column field="id" header="Bestellnummer" body={(p: any) => <>{`#${`${p.id}`.padStart(5, '0')}`}</>} sortable ></Column>
            <Column field="object_name" header="Objekt"></Column>
            <Column field="cleaner" header="Reinigungskraft"></Column>
            <Column field={'created'} header="Erstellt" sortable body={(p: any) => formatDate(p.created_utc)}></Column>
            <Column field={'updated'} header="Aktualisiert" sortable body={(p: any) => formatDate(p.updated_utc)}></Column>
            <Column header="" body={(o: any) => <Button icon="pi pi-pencil" rounded text aria-label="Filter" onClick={() => sdk.openDialog(OrderDialogStore, { order_id: o.id })} />}></Column>
            {/* <Column body={(p: any) => (<BodyCounterTemplate product={p} sdk={sdk} />)}></Column> */}
        </DataTable>
        <Paginator
            first={((sdk.currentStore?.currentPage || 1) - 1) * 30}
            rows={30}
            totalRecords={sdk.currentStore?.totalCount || 1}
            onPageChange={onPageChange}
            // rowsPerPageOptions={30}
            template="PrevPageLink CurrentPageReport NextPageLink"
            currentPageReportTemplate={`Seite {currentPage} / {totalPages} (${sdk.currentStore?.totalCount || 0} Bestellungen)`}
        />
    </div>;
});

export default OrderOverview;